/**
 * Human references — the strings people and agents read, type and quote on a bank
 * transfer. The single source of truth (`.docs/00-index.md` §5): "no regex on a ref
 * elsewhere". One table drives making, parsing, validating and the zod schemas.
 *
 * Counters are not here. Refs are minted by the API against `FOR UPDATE` counters
 * (`ARCHITECTURE.md` §Money pipeline); this file only formats and parses.
 */
import { DomainError } from './errors.js';
import { parsePeriod } from './dates.js';
export const REF_KINDS = [
    'invoice',
    'work_order',
    'request',
    'retainer_change',
    'content_plan',
    'task',
    'call',
    'widget',
    'client',
    // Session 46: a blog post, `PST-2026-0001` — per year like an invoice or a call, and
    // four digits wide because a blog writes more posts in a year than a studio raises
    // invoices.
    'post',
    // Session 48: a mockup the studio shares for feedback, `MCK-2026-001` — per year, three
    // digits, like a work order.
    'mockup',
];
/* ---------------------------------------------------------------------- the table */
/**
 * A capture group the pattern guarantees. The throw is unreachable through the
 * patterns below and exists so a future pattern that loses a group says so.
 */
function group(match, index) {
    const value = match[index];
    if (value === undefined) {
        throw new DomainError('ValidationFailed', 'A reference pattern is missing a capture group.', {
            index,
        });
    }
    return value;
}
function pad(value, width) {
    return String(value).padStart(width, '0');
}
function assertSequence(sequence) {
    if (!Number.isSafeInteger(sequence) || sequence < 1) {
        throw new DomainError('ValidationFailed', 'A reference sequence starts at 1.', {
            sequence: String(sequence),
        });
    }
    return sequence;
}
function assertYear(year) {
    if (!Number.isSafeInteger(year) || year < 1000 || year > 9999) {
        throw new DomainError('ValidationFailed', 'A reference year is between 1000 and 9999.', {
            year: String(year),
        });
    }
    return year;
}
function refSpec(spec) {
    return {
        kind: spec.kind,
        pattern: spec.pattern,
        example: spec.example,
        parseable: spec.parseable ?? true,
        make: spec.make,
        parse: (value) => {
            const match = spec.pattern.exec(value);
            return match === null ? null : { kind: spec.kind, ref: value, ...spec.read(match) };
        },
    };
}
/**
 * Sequences pad to their nominal width and accept more: the envelope in
 * `ARCHITECTURE.md` puts invoices at ~200 a month, so `FES-2026-1000` is a year
 * away, not a redesign.
 */
export const REF_TABLE = {
    invoice: refSpec({
        kind: 'invoice',
        pattern: /^FES-([1-9]\d{3})-(\d{3,})$/,
        example: 'FES-2026-047',
        make: ({ year, sequence }) => `FES-${String(assertYear(year))}-${pad(assertSequence(sequence), 3)}`,
        read: (match) => ({ year: Number(group(match, 1)), sequence: Number(group(match, 2)) }),
    }),
    work_order: refSpec({
        kind: 'work_order',
        pattern: /^WO-([1-9]\d{3})-(\d{3,})$/,
        example: 'WO-2026-008',
        make: ({ year, sequence }) => `WO-${String(assertYear(year))}-${pad(assertSequence(sequence), 3)}`,
        read: (match) => ({ year: Number(group(match, 1)), sequence: Number(group(match, 2)) }),
    }),
    request: refSpec({
        kind: 'request',
        pattern: /^REQ-(\d{3,})$/,
        example: 'REQ-119',
        make: ({ sequence }) => `REQ-${pad(assertSequence(sequence), 3)}`,
        read: (match) => ({ sequence: Number(group(match, 1)) }),
    }),
    retainer_change: refSpec({
        kind: 'retainer_change',
        pattern: /^RET-CHG-(\d{3,})$/,
        example: 'RET-CHG-004',
        make: ({ sequence }) => `RET-CHG-${pad(assertSequence(sequence), 3)}`,
        read: (match) => ({ sequence: Number(group(match, 1)) }),
    }),
    content_plan: refSpec({
        kind: 'content_plan',
        pattern: /^PLAN-([1-9]\d{3}-(?:0[1-9]|1[0-2]))$/,
        example: 'PLAN-2026-09',
        make: ({ period }) => `PLAN-${parsePeriod(period)}`,
        read: (match) => ({ period: parsePeriod(group(match, 1)) }),
    }),
    task: refSpec({
        kind: 'task',
        pattern: /^TSK-(\d{4,})$/,
        example: 'TSK-1042',
        make: ({ sequence }) => `TSK-${pad(assertSequence(sequence), 4)}`,
        read: (match) => ({ sequence: Number(group(match, 1)) }),
    }),
    call: refSpec({
        kind: 'call',
        pattern: /^CALL-([1-9]\d{3})-(\d{3,})$/,
        example: 'CALL-2026-091',
        make: ({ year, sequence }) => `CALL-${String(assertYear(year))}-${pad(assertSequence(sequence), 3)}`,
        read: (match) => ({ year: Number(group(match, 1)), sequence: Number(group(match, 2)) }),
    }),
    widget: refSpec({
        kind: 'widget',
        pattern: /^wgt_([a-z][a-z0-9]{1,15})_([0-9a-f]{4})$/,
        example: 'wgt_chat_9f3a',
        make: ({ type, hex4 }) => `wgt_${type}_${hex4}`,
        read: (match) => ({ type: group(match, 1), hex4: group(match, 2) }),
    }),
    client: refSpec({
        kind: 'client',
        // 2-40 characters, starting and ending alphanumeric so the slug is safe in a URL.
        pattern: /^[a-z0-9][a-z0-9-]{0,38}[a-z0-9]$/,
        example: 'casaturca',
        parseable: false,
        make: ({ slug }) => slug,
        read: (match) => ({ slug: group(match, 0) }),
    }),
    post: refSpec({
        kind: 'post',
        pattern: /^PST-([1-9]\d{3})-(\d{4,})$/,
        example: 'PST-2026-0001',
        make: ({ year, sequence }) => `PST-${String(assertYear(year))}-${pad(assertSequence(sequence), 4)}`,
        read: (match) => ({ year: Number(group(match, 1)), sequence: Number(group(match, 2)) }),
    }),
    mockup: refSpec({
        kind: 'mockup',
        pattern: /^MCK-([1-9]\d{3})-(\d{3,})$/,
        example: 'MCK-2026-001',
        make: ({ year, sequence }) => `MCK-${String(assertYear(year))}-${pad(assertSequence(sequence), 3)}`,
        read: (match) => ({ year: Number(group(match, 1)), sequence: Number(group(match, 2)) }),
    }),
};
const ALL_SPECS = REF_KINDS.map((kind) => REF_TABLE[kind]);
/* ------------------------------------------------------------------ make / parse */
/** Formats a reference, and refuses to hand back one its own pattern would reject. */
export function makeRef(kind, parts) {
    const spec = REF_TABLE[kind];
    const ref = spec.make(parts);
    if (!spec.pattern.test(ref)) {
        throw new DomainError('ValidationFailed', `Those parts do not make a ${kind} reference.`, {
            kind,
            ref,
        });
    }
    return ref;
}
/** True when `value` is a reference of that kind. Case-sensitive: refs are exact. */
export function isRef(kind, value) {
    return REF_TABLE[kind].pattern.test(value);
}
/* ------------------------------------------------------------------ widget refs */
/**
 * How many characters of a widget type's code go into its reference.
 *
 * `.docs/00-index.md` §8 and `ARCHITECTURE.md` §Registry both publish
 * `wgt_chat_9f3a`, and the widget type it belongs to is `chatbot`
 * (`apps/api/src/seed/catalog.ts`). Four is what turns one into the other, and it
 * turns `booking` into `book` the same way.
 */
export const WIDGET_REF_TYPE_LENGTH = 4;
/**
 * The type segment of a widget reference, from the widget type's catalog code.
 *
 * A catalog code is `[a-z][a-z0-9_]*[a-z0-9]` and the reference's segment may not
 * carry an underscore — the reference's own separator is `_`, so a segment with one
 * in it would make `wgt_a_b_9f3a` a string no parser could take apart the same way
 * twice. So the underscores come out and the first four characters of what is left
 * are the segment: `chatbot` → `chat`, `booking` → `book`, `product_finder` →
 * `prod`.
 *
 * Two codes can therefore share a segment, and that costs nothing: the four hex
 * digits after it are what make the reference unique, and the database has the
 * unique index. The segment is there so a person reading `wgt_chat_9f3a` in a log
 * knows which widget they are looking at without a lookup.
 */
export function widgetRefTypeOf(widgetTypeCode) {
    const letters = widgetTypeCode.toLowerCase().replace(/[^a-z0-9]/g, '');
    const segment = letters.slice(0, WIDGET_REF_TYPE_LENGTH);
    if (!/^[a-z][a-z0-9]?/.test(segment) || segment.length < 2) {
        throw new DomainError('ValidationFailed', 'A widget type code has to start with at least two letters or digits after a letter.', { code: widgetTypeCode });
    }
    return segment;
}
/**
 * Four hex digits from bytes somebody else generated.
 *
 * The randomness is the caller's — `node:crypto` is not available here
 * (`ARCHITECTURE.md` §Repository: `packages/shared` has no Node) — and this is the
 * one place the two hex characters per byte and the length of the segment are
 * decided.
 */
export function widgetRefHex(bytes) {
    let hex = '';
    for (const byte of bytes)
        hex += byte.toString(16).padStart(2, '0');
    const four = hex.slice(0, 4);
    if (four.length < 4) {
        throw new DomainError('ValidationFailed', 'A widget reference needs two bytes of randomness.', {
            bytes: String(bytes.length),
        });
    }
    return four;
}
/**
 * Reads any entity reference and says what it points at.
 *
 * `client` is not in the search: a slug is any lowercase word, so it matches every
 * string the other eight reject — including a lowercased invoice ref. Ask for a
 * client slug by name, with `isRef('client', …)`.
 */
export function parseRef(value) {
    for (const spec of ALL_SPECS) {
        if (!spec.parseable)
            continue;
        const parsed = spec.parse(value);
        if (parsed !== null)
            return parsed;
    }
    throw new DomainError('ValidationFailed', `"${value}" is not a reference this system issues.`, {
        value,
    });
}
/**
 * Every reference anywhere inside a value, in the order they were met, deduped.
 *
 * `.docs/07-tasks.md` §DRY watchlist: "The 'what refs are in this input' extractor
 * used by `createReviewTask` is `refs.extractAll(obj)` in `packages/shared/refs.ts`."
 * A review task records what the call it is about was *about*, and an action's input
 * is a flat bag of refs (`ARCHITECTURE.md` §Naming: "Every tool addresses entities by
 * human ref, never UUID") — so the answer is "read the input" rather than "ask each
 * action to declare which of its fields are refs", which is a list every new action
 * could forget to write.
 *
 * **Whole strings only.** `"FES-2026-047"` is a reference and `"paid FES-2026-047
 * today"` is a sentence; scanning inside strings would mean a free-text `outcome`
 * deciding what a task is filed under. `client` is not in the search for the reason
 * `parseRef` gives — every lowercase word is a valid slug.
 *
 * Cycles are handled: a value already seen is not walked twice, so an object that
 * points at itself terminates rather than exhausting the stack.
 */
export function extractAll(value) {
    const found = [];
    const seen = new Set();
    const visited = new Set();
    const walk = (node) => {
        if (typeof node === 'string') {
            for (const spec of ALL_SPECS) {
                if (!spec.parseable || !spec.pattern.test(node))
                    continue;
                if (!seen.has(node)) {
                    seen.add(node);
                    found.push(node);
                }
                return;
            }
            return;
        }
        if (typeof node !== 'object' || node === null)
            return;
        if (visited.has(node))
            return;
        visited.add(node);
        if (Array.isArray(node)) {
            for (const item of node)
                walk(item);
            return;
        }
        for (const nested of Object.values(node))
            walk(nested);
    };
    walk(value);
    return found;
}
/* ---------------------------------------------------------------------- tokens */
export const TOKEN_KINDS = ['agent', 'widget'];
/** `fes_at_…` for an agent token, `fes_wk_…` for a widget key. */
const TOKEN_INFIX = { agent: 'at', widget: 'wk' };
/** The lookup half, stored in the clear as `token_prefix` / `api_key_prefix`. */
export const TOKEN_PREFIX_PATTERN = /^[a-z0-9]{8}$/;
/** The secret half — base64url, 32 characters, never stored and never logged. */
export const TOKEN_SECRET_PATTERN = /^[A-Za-z0-9_-]{32}$/;
const TOKEN_PATTERN = /^fes_(at|wk)_([a-z0-9]{8})_([A-Za-z0-9_-]{32})$/;
/** What a redacted token says instead of its secret. */
export const REDACTED = '[redacted]';
export function formatToken(kind, prefix, secret) {
    if (!TOKEN_PREFIX_PATTERN.test(prefix)) {
        throw new DomainError('ValidationFailed', 'A token prefix is 8 lowercase alphanumerics.', {
            prefix,
        });
    }
    if (!TOKEN_SECRET_PATTERN.test(secret)) {
        // The secret is not in the meta, and it is not in the message either.
        throw new DomainError('ValidationFailed', 'A token secret is 32 base64url characters.', {});
    }
    return `fes_${TOKEN_INFIX[kind]}_${prefix}_${secret}`;
}
export function parseToken(value) {
    const match = TOKEN_PATTERN.exec(value);
    if (match === null) {
        // Deliberately no `value` in the meta: a malformed token is still a secret.
        throw new DomainError('ValidationFailed', 'That is not a Far East Studios token.', {});
    }
    const kind = group(match, 1) === 'at' ? 'agent' : 'widget';
    const prefix = group(match, 2);
    const secret = group(match, 3);
    const token = {
        kind,
        prefix,
        toString: () => `fes_${TOKEN_INFIX[kind]}_${prefix}_${REDACTED}`,
        toJSON: () => ({ kind, prefix, secret: REDACTED }),
    };
    // Non-enumerable, so `console.log(token)` and `{...token}` do not carry it either.
    Object.defineProperty(token, 'secret', {
        value: secret,
        enumerable: false,
        writable: false,
        configurable: false,
    });
    return token;
}
/* ------------------------------------------------------------- Wise matching */
/**
 * A bank transfer reference, as typed by a human, into an invoice ref or nothing.
 *
 * `PAYMENTS.md` §Matching, rule 1: "`transfer_reference` contains our invoice ref
 * (`FES-2026-001`, normalised: case, spacing, punctuation stripped) → auto-match."
 * Goldens from `studio/money.html`: `FES2026049` → `FES-2026-049`,
 * `BUILDERSTOCK ODOO` → `null`, `part` → `null`.
 */
export function normaliseReference(value) {
    const stripped = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    const match = /FES([1-9]\d{3})(\d{3,})/.exec(stripped);
    if (match === null)
        return null;
    const year = Number(group(match, 1));
    const digits = group(match, 2);
    /*
     * Stripping the punctuation runs the reference into whatever the payer typed next,
     * and the digit run is then ambiguous: `FES20261000` is `FES-2026-1000`, while the
     * same eight digits in "FES-2026-047 1st invoice" are `FES-2026-047` and a stray 1.
     * So both readings are offered to `makeRef` and the one it reproduces exactly wins —
     * a sequence is padded to three digits and never carries a leading zero beyond that,
     * which is what tells the two apart. A run that reads as a reference either way (a
     * stray digit after a four-digit sequence) takes the longer one and, being genuinely
     * ambiguous, is what rules 2 and 3 in `PAYMENTS.md` §Matching are for.
     */
    for (const candidate of [digits, digits.slice(0, 3)]) {
        const sequence = Number(candidate);
        // Counters start at 1, so `FES2026000` is noise that happens to look like a ref.
        if (sequence < 1)
            continue;
        const ref = makeRef('invoice', { year, sequence });
        if (ref === `FES-${String(year)}-${candidate}`)
            return ref;
    }
    return null;
}
//# sourceMappingURL=refs.js.map